import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { api } from '@/shared/api/axios';
import type { LeadTriageResponse, DealCoachResponse } from '../types';

// Both /api/v2/ai/* copilot endpoints are `requireAuth` (see api-v2/index.js):
//  • lead-triage is scoped to the caller's permitted Lead set server-side, so
//    every authenticated tier may call it (Req 9.2).
//  • deal-coach/:id enforces the SAME per-record RBAC as GET /api/deals/:id and
//    returns a 404/403 for a missing or out-of-scope Deal (Req 10.4, 10.5).

/**
 * Lead Triage copilot (Requirement 9). Returns priority bands with each lead's
 * underlying score plus a separate `unscored` group, all labeled `AI_Suggestion`.
 * The server derives scope from the session, so there are no query params.
 */
export function useLeadTriage(params: { enabled?: boolean } = {}) {
  const { enabled = true } = params;
  return useQuery({
    queryKey: ['ai-copilot', 'lead-triage'],
    queryFn: async () => {
      const { data } = await api.get<LeadTriageResponse>('/api/v2/ai/lead-triage');
      return data;
    },
    enabled,
    placeholderData: keepPreviousData,
  });
}

/**
 * Deal Coach copilot (Requirement 10). Fetches win-probability score, factors,
 * grounding and the next-best-action for a single Deal, labeled `AI_Suggestion`.
 *
 * A missing / out-of-scope Deal resolves to a 404 / 403 that we surface as an
 * explicit "not found" / "access denied" state (Req 10.5) rather than a generic
 * error. Retries are disabled so those expected outcomes render immediately.
 */
export function useDealCoach(params: { dealId: string; enabled?: boolean }) {
  const { dealId, enabled = true } = params;
  const trimmed = dealId.trim();
  return useQuery<DealCoachResponse, AxiosError<{ error?: string }>>({
    queryKey: ['ai-copilot', 'deal-coach', trimmed],
    queryFn: async () => {
      const { data } = await api.get<DealCoachResponse>(
        `/api/v2/ai/deal-coach/${encodeURIComponent(trimmed)}`,
      );
      return data;
    },
    enabled: enabled && trimmed.length > 0,
    retry: false,
  });
}
